import EventBus from 'diagram-js/lib/core/EventBus';
import CommandInterceptor from 'diagram-js/lib/command/CommandInterceptor';
import {NodeOpenNotes} from "@ispm-client/modules/bpmn-modeler/store/actions";
import {select, Store} from "@ngrx/store";
import {State} from "@ispm-client/modules/bpmn-modeler/store/state";
import {CommandType, EventType} from "@ispm/shared/bpmn-io/bpmn-js";
import {selectActiveNodeNotesPanel, selectNodesWithNotes} from "@ispm-client/modules/bpmn-modeler/store/selectors";
import * as _ from 'lodash';
import Selection from 'diagram-js/lib/features/selection/Selection';
import {OverlayType} from "@ispm/shared/bpmn-io/overlay-types";

export class NodeNotesIconOverlayBehavior extends CommandInterceptor {

  private iconClass = "icon__dls-notes-glyph";
  private activeNotesPanelClass = "active-notes-panel";
  private nodesWithNotes: Set<string> = new Set();
  private activeNodeNotesPanel: string;
  private selectedNode: any;

  constructor(protected eventBus: EventBus, private overlays, private store$: Store<State>, private elementRegistry,
              private config, private selection: Selection) {
    super(eventBus);
    const disableNotesPerNode = config && config.behavior && config.behavior.allowNotesPerNode === false;
    if (disableNotesPerNode) {
      return;
    }
    const self = this;
    eventBus.on(EventType.SELECTION_CHANGED, function(e) {
      const selection = e.newSelection;
      if (selection.length === 1) {
        self.selectedNode = selection[0];
      }
    });
    this.store$.pipe(select(selectNodesWithNotes)).subscribe((nodeIds) => {
      this.handleNewNodeSet(nodeIds);
    });
    this.store$.pipe(select(selectActiveNodeNotesPanel)).subscribe((nodeId) => {
      this.markActiveNodeNotesPanel(nodeId);
    });
    super.postExecuted([CommandType.SHAPE_REPLACE], (event) => {
      this.addOverlay(event.context.newShape);
    });
    eventBus.on(EventType.NODE_UPDATED, (event) => {
      if(event.node){
        this.addOverlay(event.node);
      }
    });
    eventBus.on([EventType.SHAPE_ADDED], (event) => {
      if (event.element.businessObject.markedToBeRendered) {
        this.addOverlay(event.element);
      }
    });
  }

  private getElementById(nodeId: string) {
    return this.elementRegistry.get(nodeId);
  }

  private setActiveNotesPanelClass(nodeId, active) {
    const element = this.getElementById(nodeId);
    if (element) {
      let overlay = this.getOverlay(element);
      if (!_.isEmpty(overlay)) {
        let container = overlay[0].htmlContainer;
        if (container) {
          let div = container.getElementsByClassName(this.iconClass);
          if (!_.isEmpty(div)) {
            let classList = div[0].classList;
            if (classList) {
              if (active) {
                classList.add(this.activeNotesPanelClass)
              } else {
                classList.remove(this.activeNotesPanelClass);
              }
            }
          }
        }
      }
    }
  }

  private markActiveNodeNotesPanel(nodeId: string) {
    if (this.activeNodeNotesPanel === nodeId) {
      return;
    }
    if (this.activeNodeNotesPanel) {
      this.setActiveNotesPanelClass(this.activeNodeNotesPanel, false);
    }
    if (nodeId) {
      this.setActiveNotesPanelClass(nodeId, true);
    }
    this.activeNodeNotesPanel = nodeId;
  }

  private handleNewNodeSet(nodeIds: Set<string>) {
    const newlyAddedNodes: Set<string> = new Set();
    nodeIds.forEach((nodeId) => {
      if (!this.nodesWithNotes.has(nodeId)) {
        newlyAddedNodes.add(nodeId);
      }
    });
    this.nodesWithNotes = nodeIds;
    newlyAddedNodes.forEach((nodeId) => {
      const element = this.getElementById(nodeId);
      if (element) {
        this.addOverlay(element, (nodeId === this.activeNodeNotesPanel));
      }
    })
  }

  private shouldDrawIconOnNode(id: string): boolean {
    return this.nodesWithNotes.has(id);
  }

  private checkOverlay(element) {
    return this.overlays.check({element: element, type: OverlayType.notesIconOverlay});
  }

  private getOverlay(element) {
    return this.overlays.get({element: element, type: OverlayType.notesIconOverlay});
  }

  private addOverlay(element, isActive?) {
    if (!this.shouldDrawIconOnNode(element.id)) {
      return;
    }
    let existing = this.checkOverlay(element);
    if (!_.isEmpty(existing)) {
      return;
    }
    const activeClass = isActive ? this.activeNotesPanelClass : "";
    this.overlays.add(element.id, OverlayType.notesIconOverlay, {
      position: {
        top: -10,
        left: -10
      },
      html: `<div class="node-notes-overlay-icon ${activeClass} ${this.iconClass}"></div>`
    });
    const overlay = this.getOverlay(element);
    overlay[0].htmlContainer.onclick = () => this.openNotes(element);
  }

  private openNotes(element) {
    // If a different element is currently selected, deselect it, so users don't get confused
    if (this.selectedNode && this.selectedNode.id !== element.id) {
      this.selection.deselect(this.selectedNode);
    }
    this.store$.dispatch(new NodeOpenNotes({ node: element }));
  }
}

NodeNotesIconOverlayBehavior.$inject = ['eventBus', 'overlays', 'store$', 'elementRegistry', 'config', 'selection'];
